// ipd-live.js — the Medplum data layer for IPD v2 (the in-patient ward product).
//
// ONLINE-PRIMARY, run-as-the-user: every function here resolves the ONE logged-in
// staff session via getMedplum() and reads/writes FHIR DIRECTLY as that user, so
// the per-user Medplum AccessPolicy (RLS) is the only authorization lock — exactly
// like the converted OPD screens (medplum-tasks.js / medplum-patients.js). There is
// NO Express middle tier for IPD v2 and NO service-account path here: a dose tick,
// an assessment, a handover are attributed to the nurse's own Practitioner via
// their own token (contract §2 — attribution = the user's own identity).
//
// Fail loud: a failed read/write THROWS (AGENTS §1.3 — no speculative
// try/catch-to-default). The screens render the thrown error as their designed
// error state. The offline outbox is deferred until the shared OPD outbox ships
// (contract §; these are direct online writes for v1).
//
// The pure due-engine (ipd-due.js) does the slot maths; this module only fetches
// its inputs and persists the completions that carry the slotKey extension it
// matches on. Resource shapes: IPD_V2_DATA_CONTRACT.md §2. Signatures: §3.
import { getMedplum } from './medplum-client.js';
import {
  computeDueItems, shiftFor, withinWorklistWindow,
  doseSlotKey, vitalsSlotKey, assessSlotKey, localDateStr, slotKeyOf,
  SLOT_KEY_EXT, SHIFT_EXT, FREQ_TIMES,
} from './ipd-due.js';
// Ward grouping model (pure) — the ward-config feature. listWardBeds carries the
// ward TYPE on each bed's ward projection and a grouped `wardGroups` view derived
// here so WardBoard groups/filters by ward without a second read (READ-only
// extension; the write path lives in ward-config.js).
import { wardTypeOf, wardTypeLabel, shapeWardGroups, floorProjection, FLOOR_PHYS_CODE } from './ward-grouping.js';

// ── shared constants (contract §2) ──────────────────────────────────────────
const WARD_PHYS = 'http://terminology.hl7.org/CodeSystem/location-physical-type';
const IMP_CLASS = { system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode', code: 'IMP', display: 'inpatient encounter' };
const BED_ID_SYSTEM = 'https://healther.io/bed';
const MRN_SYSTEM = 'https://healther.io/opd/mrn';
// The department-slug identifier OPD stamps on the department Organization — the
// same value the row-level policy's %department resolves to; we copy the OPD
// pattern for Encounter department stamping (contract §2).
const DEPT_SLUG_SYSTEM = 'https://healther.io/opd/department-slug';
const NURSING_ASSESSMENT_URL = 'https://healther.io/ipd/questionnaire/nursing-assessment';
// The "active shift-assessment form" designation (2026-07-19). A single boolean
// extension on ONE assessment-kind Questionnaire marks it as the form nurses fill
// each shift. Storing the designation ON the Questionnaire (a resource nurses can
// already create/update under their AccessPolicy) needs NO new resource type and
// NO policy change — see setActiveAssessmentTemplate / resolveActiveAssessmentTemplate.
const ACTIVE_ASSESSMENT_EXT = 'https://healther.io/fhir/ipd/active-assessment';
// Admission-consent stash (contract §3 admitToBed consent; see admitToBed).
const ADMIT_CONSENT_EXT = 'https://healther.io/fhir/ipd/admit-consent';
// Braden/Morse score extensions the shift-assessment writes onto the QR so Agent
// C's previous-shift strip can read them without re-fetching the score Observations.
const BRADEN_SCORE_EXT = 'https://healther.io/fhir/ipd/braden-score';
const MORSE_SCORE_EXT = 'https://healther.io/fhir/ipd/morse-score';
// Wave-2 dose-amendment extensions (contract §7b):
//  - DOSE_ADJUST_EXT rides on a MedicationAdministration whose given dose differed
//    from the order (a this-occasion adjustment, any ward role); valueString = the
//    ACTUAL dose text given. The order itself is untouched.
//  - STOPPED_AT_EXT rides on a stopped MedicationRequest so the cardex/timeline can
//    render the exact changeover instant (FHIR has no native "stopped at" element;
//    meta.lastUpdated is only a fallback because any later touch would move it).
const DOSE_ADJUST_EXT = 'https://healther.io/fhir/ipd/dose-adjusted';
const STOPPED_AT_EXT = 'https://healther.io/fhir/ipd/stopped-at';
// AI-origin provenance (DEFERRED.md → AI-origin, re-triggered): a write born from a
// confirmed AI/voice proposal carries this extension so the AI's real-world error rate
// stays measurable (wrong-value-confirmed forensics, automation-bias auditing). The
// confirming clinician is still the author of record; the human reviewed and confirmed.
// valueString = the verbatim sourceQuote the proposal came from (or 'ai-proposed').
// Hand-entered writes pass no aiOrigin → no extension. Same idiom as slotExt().
const AI_ORIGIN_EXT = 'https://healther.io/fhir/ipd/ai-origin';
const VS_CATEGORY = {
  coding: [{ system: 'http://terminology.hl7.org/CodeSystem/observation-category', code: 'vital-signs', display: 'Vital Signs' }],
};
const UOM = 'http://unitsofmeasure.org';

// The exact LOINC codes + units the OPD NurseStation writes (server/opd/vitals.mjs)
// so IPD ward vitals and OPD vitals read ONE stream (contract §2). BP is a panel
// (85354-9) with systolic/diastolic COMPONENTS; the rest are simple quantities.
// `rr` (respiratory rate) is added here — the ward records it (contract §5) and it
// shares the same timeline mapping (server/medplum-timeline.mjs: 9279-1 → rr).
const VITAL_SIMPLE = [
  { key: 'hr',   code: '8867-4',  display: 'Heart rate',          unit: 'beats/min' },
  { key: 'rr',   code: '9279-1',  display: 'Respiratory rate',    unit: '/min' },
  { key: 'spo2', code: '59408-5', display: 'Oxygen saturation',   unit: '%' },
  { key: 'temp', code: '8310-5',  display: 'Body temperature',    unit: '°C' },
];

function num(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

// The logged-in user's Practitioner ref, for attribution (performer / author).
// getProfileAsync() awaits the session resume; a missing profile means no live
// session — getMedplum() already threw NotLoggedInError before we reach here, so
// this is defensive only.
async function selfRef(medplum) {
  const profile = await medplum.getProfileAsync();
  if (!profile || !profile.id) throw new Error('no logged-in practitioner to attribute the write to');
  return { reference: `${profile.resourceType}/${profile.id}`, display: displayNameOf(profile) };
}
function displayNameOf(res) {
  const n = res && res.name && res.name[0];
  if (!n) return undefined;
  return n.text || [...(n.given || []), n.family].filter(Boolean).join(' ') || undefined;
}
function slotExt(slotKey) {
  return slotKey ? [{ url: SLOT_KEY_EXT, valueString: slotKey }] : [];
}
// The AI-origin extension for a write born from a confirmed AI proposal. `aiOrigin` is
// { sourceQuote?:string } (or falsy for a hand-entered write → no extension).
function aiOriginExt(aiOrigin) {
  if (!aiOrigin) return [];
  const q = aiOrigin.sourceQuote != null ? String(aiOrigin.sourceQuote).trim() : '';
  return [{ url: AI_ORIGIN_EXT, valueString: q || 'ai-proposed' }];
}
function shiftExt(shift) {
  return shift ? [{ url: SHIFT_EXT, valueCode: shift }] : [];
}

// ════════════════════════════════════════════════════════════════════════════
// READS
// ════════════════════════════════════════════════════════════════════════════

/**
 * listWardBeds() → { wards:[Location], beds:[{ location, bedId, label, ward,
 *   encounter|null, patient|null }] }
 *
 * The ward board's backbone: every bed Location joined to its live admission (an
 * in-progress IMP Encounter whose location[] points at the bed) and that
 * admission's Patient. Occupancy is DERIVED from Encounters (contract §2 — never
 * written onto the Location). Efficient: 2 searches for the Locations, ONE
 * Encounter search with _include=Encounter:subject so the patients come back in
 * the same round-trip.
 */
export async function listWardBeds() {
  const { medplum } = await getMedplum();

  // Wards + beds + floors in one search (physical-type wa | bd | lvl), then split.
  // Floors (physicalType 'lvl') are OPTIONAL — a hospital with none reads as before
  // (every ward resolves floor:null; no synthetic floor is ever invented).
  const locations = await medplum.searchResources('Location',
    { status: 'active', 'physical-type': `wa,bd,${FLOOR_PHYS_CODE}`, _count: 300 });
  const wards = locations.filter((l) => hasPhysType(l, 'wa'));
  const bedLocs = locations.filter((l) => hasPhysType(l, 'bd'));
  const floors = locations.filter((l) => hasPhysType(l, FLOOR_PHYS_CODE));
  const floorById = new Map(floors.map((f) => [f.id, f]));

  // Every in-progress inpatient encounter, WITH its subject Patient inlined so we
  // don't fan out one read per bed. searchResources returns only the primary
  // matches; we need the included Patients too, so use a raw search bundle.
  const bundle = await medplum.search('Encounter',
    { status: 'in-progress', class: 'IMP', _include: 'Encounter:subject', _count: 200 });
  const entries = (bundle && bundle.entry) || [];
  const encounters = entries.filter((e) => e.resource && e.resource.resourceType === 'Encounter').map((e) => e.resource);
  const patientById = new Map(entries
    .filter((e) => e.resource && e.resource.resourceType === 'Patient')
    .map((e) => [e.resource.id, e.resource]));

  // Index encounters by the bed Location they occupy (location[].location, the
  // active entry). A bed with two active encounters is a data error — first wins.
  const encByBedId = new Map();
  for (const enc of encounters) {
    for (const loc of activeLocationRefs(enc)) {
      const bedId = refId(loc);
      if (bedId && !encByBedId.has(bedId)) encByBedId.set(bedId, enc);
    }
  }

  // Allergies for every admitted patient in ONE search (so the board header can
  // show a red allergy flag without a fan-out) — indexed by patient id.
  const occupiedPatientIds = [...new Set(encounters.map((e) => refId(e.subject)).filter(Boolean))];
  const { byPatient: allergiesByPatient, unavailable: allergyUnavailable } = await allergiesForPatients(medplum, occupiedPatientIds);

  const wardById = new Map(wards.map((w) => [w.id, w]));
  const beds = bedLocs.map((bed) => {
    const enc = encByBedId.get(bed.id) || null;
    const patientRef = enc && enc.subject && enc.subject.reference;
    const patientRes = patientRef ? (patientById.get(refId(enc.subject)) || null) : null;
    const wardLoc = (bed.partOf && wardById.get(refId(bed.partOf))) || null;
    return {
      // Raw FHIR Location (contract §3: `location` is the FHIR Location, with .id).
      location: bed,
      bedId: bedIdentifierOf(bed),
      label: bed.name || bedIdentifierOf(bed) || bed.id,
      // ward:{name,type} projection (the UI reads bed.ward.name / .type). The
      // ward TYPE (general/private/icu/…) rides on the ward Location.type — see
      // ward-grouping.js. Flat (partOf-less) beds carry ward:null and fold into
      // the synthetic default group in `wardGroups` below.
      ward: wardLoc ? {
        name: wardLoc.name || null, id: wardLoc.id,
        type: wardTypeOf(wardLoc), typeLabel: wardTypeLabel(wardTypeOf(wardLoc)),
        // Floor projection {id,name,level}|null resolved through the ward's partOf →
        // floor Location (wave-B; null when the ward is on no floor / none exist).
        floor: floorProjection(floorById.get(refId(wardLoc.partOf))) || null,
        location: wardLoc,
      } : null,
      // Occupied → flat encounter/patient projections; empty → null (the UI
      // treats a falsy encounter as an empty bed).
      encounter: enc ? projectEncounterCard(enc) : null,
      patient: patientRes ? projectPatientCard(patientRes, allergiesByPatient.get(patientRes.id) || [], allergyUnavailable.has(patientRes.id)) : null,
    };
  }).sort((a, b) => String(a.label).localeCompare(String(b.label), undefined, { numeric: true }));

  // wardGroups (ADDITIVE, contract-compatible): beds grouped by ward with derived
  // per-ward occupancy for the board's section headers, filter pills and the
  // occupancy widget's per-ward breakdown. `wards` stays the raw Location[] the
  // contract documents; existing consumers (WardBoard wards[0].name) are unchanged.
  const wardGroups = shapeWardGroups({ wards, beds, floors });

  return { wards, beds, floors, wardGroups };
}

// Module-level allergy cache — allergies almost never change mid-stay, so the ward
// board reads them ONCE per patient and reuses that across its 60s poll instead of
// re-searching AllergyIntolerance on every refresh (the per-poll refetch the owner
// flagged). On a FAILED fill it now follows emergency.js readEdAllergies' M-10
// split: a 403 (a role whose AccessPolicy omits AllergyIntolerance) caches a
// CONFIRMED-empty and stops re-searching every poll; any OTHER (transient) error
// leaves the id UNCACHED and flags it allergies-UNAVAILABLE, so the ward surface
// can show an honest "unknown" affordance instead of a FALSE NKA (see
// allergiesForPatients). Keyed by patient id, never evicted within a session — a
// discharged patient's stale entry is a few strings, and the bed workspace
// (getBedContext) still does its own fresh AllergyIntolerance read, so the
// authoritative header there is never cache-bound.
const _wardAllergyCache = new Map(); // patientId -> [text]

// Allergies for a set of patient ids → { byPatient: Map(patientId → [text]),
// unavailable: Set(patientId) }. Serves cached ids from _wardAllergyCache and
// fetches only the MISSING ones in ONE search, so a steady-state board poll issues
// NO AllergyIntolerance search at all. A stable red-flag header without one read per
// bed. `unavailable` carries the ids whose read genuinely FAILED (transient) this
// call, so the caller can render "allergies unknown" instead of a false NKA.
async function allergiesForPatients(medplum, patientIds) {
  const missing = patientIds.filter((id) => id && !_wardAllergyCache.has(id));
  const unavailable = new Set();
  if (missing.length) {
    try {
      const rows = await medplum.searchResources('AllergyIntolerance',
        { patient: missing.map((id) => `Patient/${id}`).join(','), _count: 400 });
      const fetched = new Map();
      for (const a of rows) {
        const pid = refId(a.patient);
        const text = a.code && (a.code.text || (a.code.coding && a.code.coding[0] && a.code.coding[0].display));
        if (!pid || !text) continue;
        if (!fetched.has(pid)) fetched.set(pid, []);
        fetched.get(pid).push(text);
      }
      // Cache every missing id INCLUDING those with no rows, so a genuinely
      // no-allergy patient isn't re-searched each poll. Only reached on success.
      for (const id of missing) _wardAllergyCache.set(id, fetched.get(id) || []);
    } catch (e) {
      // M-10 parity with emergency.js readEdAllergies: DISTINGUISH a permanent 403
      // (a role whose AccessPolicy omits AllergyIntolerance — owner 2026-07-10) from
      // a TRANSIENT failure (network/5xx/timeout). A 403 can never succeed this
      // session, so cache a CONFIRMED-empty ([]): the header may honestly show NKA and
      // we stop re-searching every poll. EVERY OTHER failure must NOT be served as a
      // confident "no allergies" (§1.3 fail loud): leave those ids UNCACHED (the next
      // poll retries) AND mark them `unavailable` so the ward surface renders an
      // honest "allergies unknown" affordance rather than a FALSE NKA for a patient
      // who may carry a recorded penicillin allergy.
      const forbidden = e && (e.status === 403 || /forbidden/i.test((e && e.message) || ''));
      if (forbidden) {
        for (const id of missing) _wardAllergyCache.set(id, []);
      } else {
        for (const id of missing) unavailable.add(id);
      }
    }
  }
  const byPatient = new Map();
  for (const id of patientIds) if (id) byPatient.set(id, _wardAllergyCache.get(id) || []);
  return { byPatient, unavailable };
}

// ── projections (contract §3 UI-facing shapes) ─────────────────────────────
// The ward screens read flat projections (bed.encounter.periodStart,
// patient.displayName, patient.allergies:[string]) rather than raw FHIR. These
// helpers derive those flat views WITHOUT losing the raw resource — the raw
// Location stays on bed.location; encounter/patient projections also carry the
// original under `.resource` for anything that needs the full FHIR shape.
function projectEncounterCard(enc) {
  return {
    id: enc.id,
    periodStart: (enc.period && enc.period.start) || null,
    reasonText: reasonTextOf(enc),
    attendingName: attendingNameOf(enc),
    subjectId: refId(enc.subject),
    status: enc.status,
    resource: enc,
  };
}
function projectPatientCard(p, allergyTexts, allergiesUnavailable = false) {
  return {
    id: p.id,
    displayName: displayNameOf(p) || 'Patient',
    age: ageFromBirthDate(p.birthDate),
    sex: sexOf(p),
    allergies: Array.isArray(allergyTexts) ? allergyTexts : [],
    // TRUE when the allergy read FAILED (transient) for this patient this poll — the
    // ward surface must render an honest "allergies unknown" chip rather than a false
    // NKA (M-10 class). FALSE means the read genuinely succeeded (empty = confirmed
    // NKA). Only listWardBeds' bulk read sets this; the bed-workspace path (which does
    // its own fresh read) leaves it false.
    allergiesUnavailable: !!allergiesUnavailable,
    birthDate: p.birthDate || null,
    resource: p,
  };
}
function reasonTextOf(enc) {
  const rc = enc && enc.reasonCode && enc.reasonCode[0];
  return (rc && rc.text) || null;
}
function attendingNameOf(enc) {
  const parts = (enc && enc.participant) || [];
  const pprf = parts.find((pt) => (pt.type || []).some((t) => (t.coding || []).some((c) => c.code === 'PPRF'))) || parts[0];
  const ind = pprf && pprf.individual;
  return (ind && ind.display) || null;
}
function ageFromBirthDate(birthDate) {
  if (!birthDate) return null;
  const b = new Date(birthDate);
  if (Number.isNaN(b.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - b.getFullYear();
  const m = now.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < b.getDate())) age--;
  return age >= 0 ? age : null;
}
function sexOf(p) {
  const g = p && p.gender;
  if (!g) return null;
  return g === 'male' ? 'M' : g === 'female' ? 'F' : g === 'other' ? 'O' : String(g).charAt(0).toUpperCase();
}

/**
 * getBedContext(encounterId) → the full workspace bundle for one admission:
 *   { encounter, patient, bed, allergies, orders, vitalsSchedule, administrations,
 *     assessments, latestVitals, handovers, dueItems }
 * dueItems is computed by the PURE engine (ipd-due.js) from the fetched inputs
 * with now = new Date().
 *
 * Wave-2 ADDITIVE fields (contract §7b — everything above is unchanged, so
 * Worklist/handover/voice consumers keep working):
 *   timeline: [{ id, kind, at, title, detail, by, status, values, scores, shift, ref }]
 *     — ONE chronological stream for the whole stay, newest first.
 *       kind ∈ 'admission'|'discharge'|'dose'|'vitals'|'assessment'|'handover'|'order'
 *       at     ISO instant of the event (sort key)
 *       title  short human line ("Paracetamol 650mg oral BD", "Vitals recorded", …)
 *       detail secondary line (held reason, adjusted dose, order-stop reason, …)
 *       by     performer/author display name when known
 *       status kind-specific: dose 'given'|'held'; order 'started'|'stopped'
 *       values vitals only: { sbp,dbp,hr,rr,temp,spo2 }
 *       scores assessment only: { braden?, morse? }
 *       shift  assessment/handover shift code 'M'|'E'|'N'
 *   latestScores: { braden:{value,at}|null, morse:{value,at}|null }
 *     — the newest filed Braden/Morse totals (glance-header risk chips).
 */
export async function getBedContext(encounterId) {
  const { medplum } = await getMedplum();
  const encounter = await medplum.readResource('Encounter', encounterId);
  const patientId = refId(encounter.subject);
  if (!patientId) throw new Error(`Encounter/${encounterId} has no subject patient`);

  // The bed Location the encounter occupies (active entry).
  const bedRef = activeLocationRefs(encounter)[0];
  const encRef = `Encounter/${encounterId}`;
  const patRef = `Patient/${patientId}`;

  // Fan out the per-encounter and per-patient reads in parallel (independent).
  const [
    patient, bed, allergies, orders, vitalsScheduleList, administrations, assessments, observations, noteObs, handovers, roundNotes,
  ] = await Promise.all([
    medplum.readResource('Patient', patientId),
    bedRef ? medplum.readReference(bedRef).catch(() => null) : Promise.resolve(null),
    medplum.searchResources('AllergyIntolerance', { patient: patRef, _count: 100 }),
    medplum.searchResources('MedicationRequest', { encounter: encRef, _count: 200 }),
    medplum.searchResources('ServiceRequest', { encounter: encRef, code: 'vitals-monitoring', _count: 20 }),
    medplum.searchResources('MedicationAdministration', { context: encRef, _count: 400 }),
    medplum.searchResources('QuestionnaireResponse', { encounter: encRef, _count: 100 }),
    medplum.searchResources('Observation', { encounter: encRef, category: 'vital-signs', _count: 400 }),
    // Free-text nurse notes (recordNursingNote) — a plain Observation with
    // code.text 'nursing-note' (NO vital-signs category, so the vitals search
    // above misses them). A separate bounded search so they fold into the
    // timeline stream; nothing else reads these today (only the writer exists).
    medplum.searchResources('Observation', { encounter: encRef, 'code:text': 'nursing-note', _count: 100 }),
    medplum.searchResources('Composition', { encounter: encRef, type: 'nursing-handover', _count: 100 }),
    // Wave-4: the doctor's round notes (a separate Composition type) — kept OUT of
    // `handovers` (the handover tab reads that) but folded into the timeline stream.
    medplum.searchResources('Composition', { encounter: encRef, type: 'doctor-round', _count: 100 }),
  ]);

  const vitalsScheduleRaw = vitalsScheduleList[0] || null;
  const now = new Date();
  // The due-engine runs on the RAW FHIR arrays (it reads FHIR shapes directly).
  const dueItems = computeDueItems({
    orders, administrations, vitalsSchedule: vitalsScheduleRaw, observations, assessments, encounter, now,
  });

  // Attach the resolved order projection to each dose due-item (contract §1) so
  // the Due tab / dose sheet renders the drug line without a second read.
  const orderById = new Map(orders.map((o) => [o.id, o]));
  const dueItemsProjected = dueItems.map((i) => {
    if (i.kind !== 'dose') return i;
    const order = orderById.get(orderIdOfDueItem(i));
    return order ? { ...i, order: projectOrder(order) } : i;
  });

  // The active assessment template (the shift-assessment tab renders it): the
  // one a head nurse designated "Use for shift assessments", else the canonical
  // Nursing Assessment Record. See resolveActiveAssessmentTemplate.
  const assessmentTemplate = await resolveActiveAssessmentTemplate().catch(() => null);

  // Wave-2: project once, reuse for both the flat context fields AND the timeline.
  const orderProjections = orders.map(projectOrder);
  const adminProjections = administrations.map(projectAdministration);
  const vitalsRows = vitalsRowsOf(observations);

  return {
    // encounter/patient/bed as FLAT projections the glance header reads, each
    // carrying `.resource` for the raw FHIR shape.
    encounter: projectEncounterCard(encounter),
    patient: projectPatientCard(patient, allergyTextsOf(allergies)),
    bed: bed ? { id: bed.id, label: bed.name || bedIdentifierOf(bed) || bed.id, name: bed.name || null, resource: bed } : null,
    // allergies as plain strings for the red header (contract §3).
    allergies: allergyTextsOf(allergies),
    // orders as flat projections (cardex reads .name/.doseText/.route/.freq/.prn/.status/.timeOfDay).
    orders: orderProjections,
    // vitalsSchedule projection exposing .everyHours (UI) + the raw ServiceRequest.
    vitalsSchedule: vitalsScheduleRaw
      ? { everyHours: periodHoursOfSchedule(vitalsScheduleRaw), resource: vitalsScheduleRaw, id: vitalsScheduleRaw.id }
      : null,
    // administrations: RAW FHIR resources with flat fields ADDED (slotKey/status/
    // effectiveDateTime/statusReason/performer/request) — contract §3 requires BOTH.
    administrations: adminProjections,
    assessments,
    // recent vitals table (contract §3: newest-first rows grouped by slot/time).
    vitalsRows,
    observations, // raw stream (handover digest + fallbacks read this)
    latestVitals: latestVitalsOf(observations),
    handovers,
    assessmentTemplate,
    dueItems: dueItemsProjected,
    // Wave-2 (contract §7b) — the whole-stay chronological stream + latest risk
    // scores. Both ADDITIVE: nothing above changed shape.
    timeline: buildTimeline({
      encounter, orders: orderProjections, administrations: adminProjections,
      vitalsRows, assessments, handovers, roundNotes, notes: noteObs,
    }),
    latestScores: latestScoresOf(assessments),
    asOf: now.toISOString(),
  };
}

/**
 * listWorklist() → { items:[dueItem & { patient, bedLabel, encounterId }], asOf }
 * Every DUE/OVERDUE action across ALL occupied beds, flattened for the nurse's
 * cross-patient worklist. Builds on listWardBeds() (the occupied set), then reads
 * each admission's due inputs and merges. Only actionable slots (due/overdue and
 * PRN actions) are returned — done/held/upcoming are dropped so the worklist is
 * the "what needs doing now" list.
 */
export async function listWorklist() {
  const { medplum } = await getMedplum();
  const { beds } = await listWardBeds();
  const occupied = beds.filter((b) => b.encounter && b.patient);
  const now = new Date();

  const perBed = await Promise.all(occupied.map(async (b) => {
    const encId = b.encounter.id;
    const encRef = `Encounter/${encId}`;
    const [orders, vitalsScheduleList, administrations, assessments, observations] = await Promise.all([
      medplum.searchResources('MedicationRequest', { encounter: encRef, _count: 200 }),
      medplum.searchResources('ServiceRequest', { encounter: encRef, code: 'vitals-monitoring', _count: 20 }),
      medplum.searchResources('MedicationAdministration', { context: encRef, _count: 400 }),
      medplum.searchResources('QuestionnaireResponse', { encounter: encRef, _count: 100 }),
      medplum.searchResources('Observation', { encounter: encRef, category: 'vital-signs', _count: 400 }),
    ]);
    const due = computeDueItems({
      orders, administrations, vitalsSchedule: vitalsScheduleList[0] || null,
      observations, assessments, encounter: b.encounter, now,
    });
    // Resolve each dose item's order projection so the worklist row can render
    // the drug line (name/dose/route/freq) without a second read (contract §1:
    // "dose items MUST carry a resolved `order` projection").
    const orderById = new Map(orders.map((o) => [o.id, o]));
    return due
      // Actionable now (due/overdue/PRN) AND within the 24h-back/2h-ahead
      // worklist window — older incomplete slots stay in computeDueItems (the
      // cardex per-day history) but are NOT worklist rows (product decision).
      .filter((i) => (i.status === 'due' || i.status === 'overdue' || i.prn) && withinWorklistWindow(i, now))
      .map((i) => {
        const order = i.kind === 'dose' ? orderById.get(orderIdOfDueItem(i)) : null;
        return {
          ...i,
          patient: b.patient, // already a projection ({id, displayName, …})
          bedLabel: b.label,
          encounterId: encId,
          ...(order ? { order: projectOrder(order) } : {}),
        };
      });
  }));

  const items = perBed.flat().sort(worklistOrder);
  return { items, asOf: now.toISOString() };
}

// Overdue first, then due, then PRN; within a bucket, earliest dueAt first.
function worklistOrder(a, b) {
  const rank = (i) => (i.status === 'overdue' ? 0 : i.status === 'due' ? 1 : 2);
  const r = rank(a) - rank(b);
  if (r !== 0) return r;
  return String(a.dueAt || '~').localeCompare(String(b.dueAt || '~'));
}

/** listAssessmentTemplates() → Questionnaire[] — the nursing-assessment templates
 *  (all versions of the ward assessment form; the newest active one is used). */
export async function listAssessmentTemplates() {
  const { medplum } = await getMedplum();
  return medplum.searchResources('Questionnaire',
    { url: NURSING_ASSESSMENT_URL, _sort: '-_lastUpdated', _count: 20 });
}

// True iff this Questionnaire carries the "active shift-assessment form" marker.
function isActiveAssessmentTemplate(q) {
  const e = ((q && q.extension) || []).find((x) => x && x.url === ACTIVE_ASSESSMENT_EXT);
  return !!(e && e.valueBoolean === true);
}

/**
 * activeAssessmentOf(templates) → Questionnaire | null  (pure).
 * Given a list of IPD templates (each an assessment/symptom/checklist Questionnaire),
 * return the ONE assessment-kind form nurses fill each shift: the explicitly
 * designated one (the marker extension), else the canonical Nursing Assessment
 * Record, else the newest assessment form. Shared by the resolver (below) and the
 * template editor's "Active" affordance so both agree on which form is active.
 */
export function activeAssessmentOf(templates) {
  const arr = (Array.isArray(templates) ? templates : []).filter((q) => templateKindOf(q) === 'assessment');
  const marked = arr.find(isActiveAssessmentTemplate);
  if (marked) return marked;
  return arr.find((q) => q.url === NURSING_ASSESSMENT_URL) || arr[0] || null;
}

/**
 * resolveActiveAssessmentTemplate() → Questionnaire | null.
 * The active shift-assessment form used by getBedContext + the ShiftAssessment tab.
 * Reads every active IPD assessment-kind Questionnaire and applies activeAssessmentOf.
 * Fallback-safe: with no designation it returns the canonical Nursing Assessment
 * Record exactly as before, so untouched wards behave identically.
 */
export async function resolveActiveAssessmentTemplate() {
  const { medplum } = await getMedplum();
  const all = await medplum.searchResources('Questionnaire',
    { status: 'active', _sort: '-_lastUpdated', _count: 100 });
  const ipd = all.filter((q) => String(q.url || '').startsWith(IPD_Q_URL_PREFIX));
  return activeAssessmentOf(ipd);
}

// ════════════════════════════════════════════════════════════════════════════
// WRITES
// ════════════════════════════════════════════════════════════════════════════

/**
 * admitToBed({ bedLocationId, patientId | newPatient, condition, allergies,
 *   attendingRef, vitalsEveryHours=4 }) → { encounter, patient }
 *
 * Opens an in-progress IMP Encounter on the given bed for an existing patient
 * (reuse by patientId — the "patient twice" guard, AGENTS §3) or a freshly
 * created one. Stamps the department onto the Encounter (copying the OPD pattern:
 * serviceProvider = the bed's ward-department org when resolvable, plus a
 * dept-slug identifier on the encounter is NOT invented here — OPD uses
 * serviceProvider, so we mirror that). Also opens the q-N-hourly vitals
 * ServiceRequest and writes any admit-time allergies.
 */
export async function admitToBed({ bedLocationId, patientId, newPatient, condition, allergies = [], attendingRef, vitalsEveryHours = 4, consent } = {}) {
  if (!bedLocationId) throw new Error('admitToBed requires bedLocationId');
  const { medplum } = await getMedplum();

  const bed = await medplum.readResource('Location', bedLocationId);

  // Patient: reuse when given an id (never re-key by name); else create.
  let patient;
  if (patientId) {
    patient = await medplum.readResource('Patient', patientId);
    // Double-admit guard (contract §2 "patient twice") — a last-line server-side
    // check the instant before we create the Encounter. The AdmitFlow UI guard runs
    // at patient-select time, leaving a window in which a second admit could land;
    // re-checking here narrows that race to the gap between this search and the
    // create below. If this patient already holds an in-progress IMP Encounter,
    // refuse loudly rather than open a second bed for them. (Runs only on the
    // existing-patient path — a just-created new patient cannot already be admitted,
    // and throwing after the create would strand an orphan Patient.)
    const openAdmissions = await medplum.searchResources('Encounter',
      { subject: `Patient/${patient.id}`, class: 'IMP', status: 'in-progress', _count: 1 });
    if (Array.isArray(openAdmissions) && openAdmissions.length) {
      const e = new Error('This patient is already admitted. Discharge the current admission before re-admitting.');
      e.alreadyAdmitted = true;
      throw e;
    }
  } else if (newPatient && newPatient.name) {
    patient = await medplum.createResource(buildNewPatient(newPatient));
  } else {
    throw new Error('admitToBed requires patientId or newPatient:{name,…}');
  }

  // Department stamping: the bed's ward carries partOf → the department org (OPD
  // pattern uses Encounter.serviceProvider = the dept org). Resolve it best-effort;
  // an admission is never blocked by a missing dept ref (the visit comes first).
  const serviceProvider = await resolveBedDepartment(medplum, bed).catch(() => null);

  const nowIso = new Date().toISOString();
  const encounter = await medplum.createResource({
    resourceType: 'Encounter',
    status: 'in-progress',
    class: IMP_CLASS,
    subject: { reference: `Patient/${patient.id}`, display: displayNameOf(patient) },
    period: { start: nowIso },
    location: [{ location: { reference: `Location/${bed.id}`, display: bed.name }, status: 'active', period: { start: nowIso } }],
    ...(attendingRef ? { participant: [{ type: [{ coding: [{ system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType', code: 'PPRF', display: 'primary performer' }] }], individual: normRef(attendingRef) }] } : {}),
    ...(condition ? { reasonCode: [{ text: condition }] } : {}),
    ...(serviceProvider ? { serviceProvider } : {}),
    // Admission consent (contract §3 admitToBed `consent:{captured,grantedBy}`).
    // FHIR-CORRECTNESS CHOICE (documented): stashed as an Encounter extension, not
    // a separate FHIR Consent resource. Consent is READONLY under the doctor/nurse
    // AccessPolicy (a deliberate write boundary), so a Consent create would 403 for
    // the very nurse who admits; the admission must never be blocked by that. The
    // extension keeps the captured flag + grantedBy WITH the admission it belongs
    // to, and is trivially promotable to a Consent resource by a Bot later.
    ...(consent && consent.captured ? {
      extension: [{
        url: ADMIT_CONSENT_EXT,
        extension: [
          { url: 'captured', valueBoolean: true },
          ...(consent.grantedBy ? [{ url: 'grantedBy', valueString: String(consent.grantedBy) }] : []),
          { url: 'capturedAt', valueDateTime: nowIso },
        ],
      }],
    } : {}),
  });

  // Vitals schedule (one ServiceRequest per encounter).
  await setVitalsFrequency({ encounterId: encounter.id, patientId: patient.id, everyHours: vitalsEveryHours });

  // Admit-time allergies (reuse the OPD-shaped AllergyIntolerance: code.text +
  // patient + active clinicalStatus).
  for (const a of allergies || []) {
    const text = typeof a === 'string' ? a : (a && a.text);
    if (!text) continue;
    await medplum.createResource(buildAllergy(patient.id, text));
  }

  return { encounter, patient };
}

/** dischargeFromBed({ encounterId }) → Encounter — flip to finished, close the
 *  period + the active bed location entry (contract §2). */
export async function dischargeFromBed({ encounterId } = {}) {
  if (!encounterId) throw new Error('dischargeFromBed requires encounterId');
  const { medplum } = await getMedplum();
  const enc = await medplum.readResource('Encounter', encounterId);
  const nowIso = new Date().toISOString();
  enc.status = 'finished';
  enc.period = { ...(enc.period || {}), end: nowIso };
  enc.location = (enc.location || []).map((l) =>
    l.status === 'active' ? { ...l, status: 'completed', period: { ...(l.period || {}), end: nowIso } } : l);
  return medplum.updateResource(enc);
}

/**
 * createMedOrder({ encounterId, patientId, name, doseText, route, freq,
 *   timeOfDay?, prn=false }) → MedicationRequest
 * Stores the EXPLICIT timeOfDay at creation (contract §2) so the due-engine never
 * re-derives — default from the FREQ grid when not supplied. PRN / STAT carry
 * asNeededBoolean / no scheduled times respectively.
 */
export async function createMedOrder({ encounterId, patientId, name, doseText, route, freq, timeOfDay, prn = false, aiOrigin } = {}) {
  if (!encounterId || !patientId || !name || !freq) throw new Error('createMedOrder requires encounterId, patientId, name, freq');
  const { medplum } = await getMedplum();
  return medplum.createResource(buildMedOrderResource({ encounterId, patientId, name, doseText, route, freq, timeOfDay, prn, aiOrigin }));
}

// The one MedicationRequest builder — createMedOrder AND replaceMedOrder emit the
// identical shape (contract §2). Carries authoredOn so the cardex day pager /
// timeline know exactly when an order started (a replacement starts mid-stay).
// `aiOrigin` (optional) stamps the AI-origin extension when the order was born from
// a confirmed AI/voice round proposal — same idiom as every other ipd-live write.
function buildMedOrderResource({ encounterId, patientId, name, doseText, route, freq, timeOfDay, prn = false, aiOrigin }) {
  const FREQ = String(freq).toUpperCase();
  const isPrn = prn || FREQ === 'PRN';
  const isStat = FREQ === 'STAT';
  // Resolve the explicit timeOfDay grid unless PRN/STAT (which have none). FHIR
  // `time` requires HH:mm:ss — expand on write (the due-engine .slice(0,5)-normalises
  // back, so slotKeys stay HH:mm and the unit tests hold).
  let times = Array.isArray(timeOfDay) && timeOfDay.length ? timeOfDay.slice() : (FREQ_TIMES[FREQ] || []);
  if (isPrn || isStat) times = [];
  times = times.map((t) => (String(t).length === 5 ? `${t}:00` : String(t)));

  const dosage = {
    text: [name, doseText, route, FREQ].filter(Boolean).join(' '),
    ...(route ? { route: { text: route } } : {}),
    ...(doseText ? { doseAndRate: [{ doseQuantity: parseDose(doseText) || undefined, doseText: parseDose(doseText) ? undefined : doseText }] } : {}),
    timing: {
      code: { text: FREQ },
      ...(times.length ? { repeat: { timeOfDay: times } } : {}),
    },
    ...(isPrn ? { asNeededBoolean: true } : {}),
  };

  const ext = aiOriginExt(aiOrigin);
  return {
    resourceType: 'MedicationRequest',
    status: 'active', intent: 'order',
    authoredOn: new Date().toISOString(),
    subject: { reference: `Patient/${patientId}` },
    encounter: { reference: `Encounter/${encounterId}` },
    medicationCodeableConcept: { text: name },
    dosageInstruction: [dosage],
    ...(ext.length ? { extension: ext } : {}),
  };
}

/** stopMedOrder({ orderId, reason }) → MedicationRequest — status:'stopped' (the
 *  due-engine stops generating new slots; already-given ticks persist). Stamps the
 *  stopped-at extension so the cardex/timeline render the exact stop point. */
export async function stopMedOrder({ orderId, reason } = {}) {
  if (!orderId) throw new Error('stopMedOrder requires orderId');
  const { medplum } = await getMedplum();
  const order = await medplum.readResource('MedicationRequest', orderId);
  markOrderStopped(order, reason);
  return medplum.updateResource(order);
}

// Flip a MedicationRequest to stopped in place: status + optional reason + the
// stopped-at instant (shared by stopMedOrder and replaceMedOrder).
// NB: MedicationRequest.statusReason is 0..1 (a SINGLE CodeableConcept) in FHIR
// R4 — unlike MedicationAdministration.statusReason (0..*). Medplum validates
// this strictly ("Expected single value for property").
function markOrderStopped(order, reason) {
  order.status = 'stopped';
  if (reason) order.statusReason = { text: reason };
  order.extension = [
    ...(order.extension || []).filter((e) => e && e.url !== STOPPED_AT_EXT),
    { url: STOPPED_AT_EXT, valueDateTime: new Date().toISOString() },
  ];
}

/**
 * replaceMedOrder({ orderId, changes:{ doseText?, route?, freq?, timeOfDay? }, reason? })
 *   → { stopped, replacement }
 *
 * The "from now on" prescription change (contract §7b): the old MedicationRequest
 * is stopped (never edited in place — its given/held record stays exactly as
 * prescribed at the time) and a NEW order is created with the changes merged in,
 * linked back via priorPrescription. The drug itself never changes here — a
 * different drug is a new order, not an amendment.
 *
 * This is a PRESCRIPTION act: the UI gates it to doctor/owner/admin (a nurse sees
 * it disabled — "Prescription change — doctor"); RLS is the enforcement layer.
 * Order of writes: replacement FIRST, then stop the old — never a gap with NO
 * active order (a silent missed dose). If stopping the old order then fails, the
 * just-created replacement is DELETED before rethrowing, so a failure can't leave
 * two active orders double-dosing the patient (observed in verification: a stop
 * validation error would otherwise orphan an active duplicate).
 */
export async function replaceMedOrder({ orderId, changes = {}, reason, aiOrigin } = {}) {
  if (!orderId) throw new Error('replaceMedOrder requires orderId');
  const hasChange = ['doseText', 'route', 'freq', 'timeOfDay'].some((k) => changes[k] !== undefined);
  if (!hasChange) throw new Error('replaceMedOrder requires at least one change (doseText, route, freq, timeOfDay)');
  const { medplum } = await getMedplum();
  const old = await medplum.readResource('MedicationRequest', orderId);
  if (old.status !== 'active') throw new Error('Only an active order can be replaced');
  const cur = projectOrder(old);
  const encounterId = refId(old.encounter);
  const patientId = refId(old.subject);
  if (!encounterId || !patientId) throw new Error('The order is missing its encounter/patient reference');

  const nextFreq = changes.freq !== undefined ? changes.freq : cur.freq;
  const replacement = await medplum.createResource({
    ...buildMedOrderResource({
      encounterId, patientId,
      name: cur.name,
      doseText: changes.doseText !== undefined ? changes.doseText : cur.doseText,
      route: changes.route !== undefined ? changes.route : cur.route,
      freq: nextFreq,
      // Explicit times win; else keep the old grid ONLY when the FREQ is unchanged
      // (a new FREQ re-derives its default grid — old times would be wrong).
      timeOfDay: changes.timeOfDay !== undefined ? changes.timeOfDay
        : (nextFreq === cur.freq ? cur.timeOfDay : undefined),
      prn: cur.prn,
      aiOrigin,
    }),
    priorPrescription: { reference: `MedicationRequest/${old.id}` },
  });

  markOrderStopped(old, reason || 'Replaced — prescription change');
  let stopped;
  try {
    stopped = await medplum.updateResource(old);
  } catch (stopErr) {
    // Compensate: without this, the patient has TWO active orders (double-dose
    // risk). Best-effort delete of the replacement, then fail loud with the
    // original error; if even the delete fails, say so explicitly.
    try {
      await medplum.deleteResource('MedicationRequest', replacement.id);
    } catch (_) {
      throw new Error(`Could not stop the old order (${stopErr.message}) AND could not remove the new one — ` +
        `BOTH orders are currently active. Stop one manually.`);
    }
    throw stopErr;
  }
  return { stopped, replacement };
}

/**
 * recordDoseTick({ order, encounterId, patientId, slotKey, outcome, reason?,
 *   adjustedDoseText?, note? }) → MedicationAdministration
 * given → status:'completed'; held/refused/unavailable → status:'not-done' +
 * statusReason. Carries the slotKey extension (contract §1 — completion matching)
 * and the nurse's own performer (contract §2 — own-token attribution).
 *
 * Wave-2 ("this dose only", any ward role — contract §7b): pass adjustedDoseText
 * when the ACTUAL dose given differs from the order. The administration's
 * dosage.text records the actual dose, the dose-adjusted extension marks it so
 * the cardex/timeline render it distinctly, and the ORDER IS UNTOUCHED. An
 * optional `note` lands on MedicationAdministration.note (the why).
 */
export async function recordDoseTick({ order, encounterId, patientId, slotKey, outcome, reason, adjustedDoseText, note, aiOrigin } = {}) {
  if (!order || !order.id) throw new Error('recordDoseTick requires the order resource');
  if (!encounterId || !patientId) throw new Error('recordDoseTick requires encounterId and patientId');
  if (!slotKey) throw new Error('recordDoseTick requires the slotKey');
  const { medplum } = await getMedplum();
  const me = await selfRef(medplum);
  const nowIso = new Date().toISOString();
  const given = outcome === 'given';
  const override = adjustedDoseText && String(adjustedDoseText).trim() ? String(adjustedDoseText).trim() : null;
  // Copy the ordered dosage onto the administration. FHIR constraint mad-1: if a
  // `dosage` element is present it SHALL carry a `dose` or `rate`. So we build the
  // dosage from the order's structured dose (doseAndRate.doseQuantity, or a dose
  // parsed from doseText) and OMIT the dosage element entirely when no structured
  // dose exists (a bare {text} violates mad-1). Tolerant of a raw MedicationRequest
  // (dosageInstruction[0]) OR the flat order projection (.doseText).
  // A this-dose-only ADJUSTMENT (contract §7b) replaces BOTH the text and — when
  // parseable — the quantity: the administration records what was ACTUALLY given.
  const di = (order.dosageInstruction && order.dosageInstruction[0]) || null;
  const orderedText = (di && di.text) || (order.name ? [order.name, order.doseText].filter(Boolean).join(' ') : undefined);
  const drugName = (order.medicationCodeableConcept && order.medicationCodeableConcept.text) || order.name || null;
  const dosageText = override
    ? [drugName, override, (di && di.route && di.route.text) || order.route].filter(Boolean).join(' ')
    : orderedText;
  const orderedDoseQty = (di && di.doseAndRate && di.doseAndRate[0] && di.doseAndRate[0].doseQuantity)
    || parseDose(order.doseText)
    || parseDose(orderedText && orderedText.replace(/^\D*/, ''));
  const doseQty = (override ? parseDose(override) : null) || orderedDoseQty;
  const dosage = doseQty
    ? { ...(dosageText ? { text: dosageText } : {}), dose: normDoseQuantity(doseQty) }
    : null;

  return medplum.createResource({
    resourceType: 'MedicationAdministration',
    status: given ? 'completed' : 'not-done',
    ...(given ? {} : { statusReason: [{ text: reason || outcome }] }),
    request: { reference: `MedicationRequest/${order.id}` },
    medicationCodeableConcept: (order.medicationCodeableConcept)
      || (order.name ? { text: order.name } : undefined),
    subject: { reference: `Patient/${patientId}` },
    context: { reference: `Encounter/${encounterId}` },
    effectiveDateTime: nowIso,
    performer: [{ actor: me }],
    ...(dosage ? { dosage } : {}),
    ...(note && String(note).trim() ? { note: [{ text: String(note).trim() }] } : {}),
    extension: [
      ...slotExt(slotKey),
      ...(override ? [{ url: DOSE_ADJUST_EXT, valueString: override }] : []),
      ...aiOriginExt(aiOrigin),
    ],
  });
}
// A doseQuantity for MedicationAdministration.dosage.dose: ensure a UCUM system so
// it validates as a proper Quantity (mad-1 needs dose present; a value is enough).
function normDoseQuantity(q) {
  if (!q || q.value == null) return { value: 1 };
  return { value: q.value, ...(q.unit ? { unit: q.unit, system: UOM, code: q.unit } : {}) };
}

/**
 * setVitalsFrequency({ encounterId, patientId, everyHours }) → ServiceRequest
 * One vitals-monitoring ServiceRequest per encounter — CREATED once, UPDATED in
 * place thereafter (contract §2 — "one per encounter, update in place").
 */
export async function setVitalsFrequency({ encounterId, patientId, everyHours } = {}) {
  if (!encounterId || !patientId) throw new Error('setVitalsFrequency requires encounterId and patientId');
  const n = num(everyHours);
  if (!n || n <= 0) throw new Error('setVitalsFrequency requires a positive everyHours');
  const { medplum } = await getMedplum();
  const encRef = `Encounter/${encounterId}`;
  const existing = await medplum.searchOne('ServiceRequest', { encounter: encRef, code: 'vitals-monitoring' });
  const timing = { occurrenceTiming: { repeat: { frequency: 1, period: n, periodUnit: 'h' } } };
  if (existing) {
    existing.occurrenceTiming = timing.occurrenceTiming;
    existing.status = 'active';
    return medplum.updateResource(existing);
  }
  return medplum.createResource({
    resourceType: 'ServiceRequest',
    status: 'active', intent: 'order',
    code: { text: 'vitals-monitoring' },
    subject: { reference: `Patient/${patientId}` },
    encounter: { reference: encRef },
    ...timing,
  });
}

/**
 * recordVitals({ encounterId, patientId, slotKey?, values:{ sbp,dbp,hr,rr,temp,spo2 } })
 *   → Observation[]
 * Writes the reading as standard vital-signs Observations using the EXACT LOINC
 * codes/units OPD writes (contract §2) so trends read one stream: a BP panel
 * (85354-9 with systolic 8480-6 / diastolic 8462-4 components) + one Observation
 * per simple vital. A scheduled round passes its slotKey → stamped on EVERY
 * Observation in the round so the due-engine matches the slot.
 */
export async function recordVitals({ encounterId, patientId, slotKey, values = {}, aiOrigin } = {}) {
  if (!encounterId || !patientId) throw new Error('recordVitals requires encounterId and patientId');
  const { medplum } = await getMedplum();
  const me = await selfRef(medplum);
  const nowIso = new Date().toISOString();
  const subject = { reference: `Patient/${patientId}` };
  const encounter = { reference: `Encounter/${encounterId}` };
  // slotKey + AI-origin extensions ride on EVERY Observation in the reading.
  const ext = [...slotExt(slotKey), ...aiOriginExt(aiOrigin)];
  const base = { resourceType: 'Observation', status: 'final', category: [VS_CATEGORY], subject, encounter, effectiveDateTime: nowIso, performer: [me], ...(ext.length ? { extension: ext } : {}) };

  const toWrite = [];
  const sys = num(values.sbp);
  const dia = num(values.dbp);
  if (sys != null || dia != null) {
    toWrite.push({
      ...base,
      code: { coding: [{ system: 'http://loinc.org', code: '85354-9', display: 'Blood pressure panel' }], text: 'Blood pressure' },
      component: [
        sys != null && { code: { coding: [{ system: 'http://loinc.org', code: '8480-6', display: 'Systolic BP' }] }, valueQuantity: { value: sys, unit: 'mmHg', system: UOM } },
        dia != null && { code: { coding: [{ system: 'http://loinc.org', code: '8462-4', display: 'Diastolic BP' }] }, valueQuantity: { value: dia, unit: 'mmHg', system: UOM } },
      ].filter(Boolean),
    });
  }
  for (const v of VITAL_SIMPLE) {
    const val = num(values[v.key]);
    if (val == null) continue;
    toWrite.push({
      ...base,
      code: { coding: [{ system: 'http://loinc.org', code: v.code, display: v.display }], text: v.display },
      valueQuantity: { value: val, unit: v.unit, system: UOM },
    });
  }
  if (!toWrite.length) throw new Error('recordVitals: no values to record');

  const out = [];
  for (const o of toWrite) out.push(await medplum.createResource(o));
  return out;
}

/** saveAssessmentTemplate(questionnaire) → Questionnaire — bumps version + updates
 *  in place (same id) if the canonical url already exists, else creates. Responses
 *  pin {url}|{version} (contract §2). */
export async function saveAssessmentTemplate(questionnaire) {
  if (!questionnaire || questionnaire.resourceType !== 'Questionnaire') throw new Error('saveAssessmentTemplate requires a Questionnaire');
  const { medplum } = await getMedplum();
  // listIpdTemplates() annotates resources with a flat `kind` for the UI; that
  // annotation is NOT FHIR (Medplum rejects "Questionnaire.kind") — the kind
  // lives in the template-kind extension. This writer is the one door to
  // Medplum for templates, so it strips UI annotations before writing.
  const { kind: _uiKind, ...clean } = questionnaire;
  const url = clean.url || NURSING_ASSESSMENT_URL;
  const existing = await medplum.searchOne('Questionnaire', { url, _sort: '-_lastUpdated' });
  if (existing) {
    const nextVersion = bumpVersion(existing.version);
    return medplum.updateResource({ ...clean, id: existing.id, url, version: nextVersion, status: 'active' });
  }
  return medplum.createResource({ ...clean, url, version: clean.version || '1', status: 'active' });
}

/**
 * setActiveAssessmentTemplate(target) → Questionnaire — designate ONE assessment
 * form as the shift-assessment nurses fill, clearing the marker from every other
 * assessment form (single-active). `target` is a Questionnaire (or {id}/{url}).
 *
 * Writes the marker extension (not a new resource type) so it stays inside the
 * nurse's existing Questionnaire create/update AccessPolicy — no policy change.
 * The template-kind extension and all other content are preserved; version is NOT
 * bumped (designation is not a content edit, and filled responses pin url|version).
 */
export async function setActiveAssessmentTemplate(target) {
  const url = typeof target === 'string' ? null : (target && target.url) || null;
  const id = typeof target === 'string' ? target : (target && target.id) || null;
  if (!url && !id) throw new Error('setActiveAssessmentTemplate requires a Questionnaire (id or url)');
  const { medplum } = await getMedplum();
  const all = await medplum.searchResources('Questionnaire',
    { status: 'active', _sort: '-_lastUpdated', _count: 100 });
  const assessments = all.filter((q) =>
    String(q.url || '').startsWith(IPD_Q_URL_PREFIX) && templateKindOf(q) === 'assessment');
  const chosen = assessments.find((q) => (id && q.id === id) || (url && q.url === url));
  if (!chosen) throw new Error('That assessment form no longer exists — reload templates and try again.');

  let result = chosen;
  for (const q of assessments) {
    const shouldBeActive = q.id === chosen.id;
    if (isActiveAssessmentTemplate(q) === shouldBeActive) continue; // already correct — no write
    const others = (q.extension || []).filter((x) => x && x.url !== ACTIVE_ASSESSMENT_EXT);
    const nextExt = shouldBeActive ? [...others, { url: ACTIVE_ASSESSMENT_EXT, valueBoolean: true }] : others;
    const updated = await medplum.updateResource({ ...q, extension: nextExt.length ? nextExt : undefined });
    if (shouldBeActive) result = updated;
  }
  return result;
}

/**
 * saveShiftAssessment({ encounterId, patientId, questionnaire, shift, date, answers,
 *   scores:{braden?,morse?} }) → QuestionnaireResponse
 * One per encounter per shift per day. Carries the slotKey + shift extensions
 * (contract §1). Braden/Morse totals are ALSO written as plain Observations
 * (code.text braden-score/morse-score, valueInteger, slotKey ext) so trends/alerts
 * read plain Observations (contract §2).
 */
export async function saveShiftAssessment({ encounterId, patientId, questionnaire, shift, date, answers = {}, scores = {}, aiOrigin } = {}) {
  if (!encounterId || !patientId) throw new Error('saveShiftAssessment requires encounterId and patientId');
  if (!shift) throw new Error('saveShiftAssessment requires shift (M|E|N)');
  const { medplum } = await getMedplum();
  const me = await selfRef(medplum);
  const dateStr = date || localDateStr(new Date());
  const slotKey = assessSlotKey(encounterId, dateStr, shift);
  const canonical = questionnaireCanonical(questionnaire);

  // Braden/Morse totals ALSO ride on the QR as valueInteger extensions (contract
  // §3) so the previous-shift strip reads them straight off the QuestionnaireResponse
  // (in addition to the plain score Observations written below for trends/alerts).
  const bradenV = num(scores.braden);
  const morseV = num(scores.morse);
  const scoreExts = [
    ...(bradenV != null ? [{ url: BRADEN_SCORE_EXT, valueInteger: bradenV }] : []),
    ...(morseV != null ? [{ url: MORSE_SCORE_EXT, valueInteger: morseV }] : []),
  ];

  const qr = await medplum.createResource({
    resourceType: 'QuestionnaireResponse',
    ...(canonical ? { questionnaire: canonical } : {}),
    status: 'completed',
    subject: { reference: `Patient/${patientId}` },
    encounter: { reference: `Encounter/${encounterId}` },
    authored: new Date().toISOString(),
    author: me,
    item: answersToItems(answers),
    // AI-origin marks the QR when any answers were prefilled from a confirmed voice
    // proposal (the nurse still reviewed + signed). Hand-filled → no extension.
    extension: [...slotExt(slotKey), ...shiftExt(shift), ...scoreExts, ...aiOriginExt(aiOrigin)],
  });

  // Braden + Morse totals as plain Observations (trends/alerts).
  const scoreObs = [];
  for (const [key, code] of [['braden', 'braden-score'], ['morse', 'morse-score']]) {
    const v = num(scores[key]);
    if (v == null) continue;
    scoreObs.push(await medplum.createResource({
      resourceType: 'Observation', status: 'final',
      code: { text: code },
      subject: { reference: `Patient/${patientId}` },
      encounter: { reference: `Encounter/${encounterId}` },
      effectiveDateTime: new Date().toISOString(),
      performer: [me],
      valueInteger: v,
      extension: [...slotExt(slotKey), ...shiftExt(shift)],
    }));
  }
  qr._scoreObservations = scoreObs; // non-FHIR convenience for the caller; not persisted.
  return qr;
}

/**
 * saveHandover({ encounterId, patientId, shift, date, html }) → Composition
 * The confirmed shift handover — one nursing-handover Composition with an XHTML
 * section (contract §2). The digest is auto-composed + nurse-confirmed upstream;
 * here we persist the confirmed HTML.
 */
export async function saveHandover({ encounterId, patientId, shift, date, html } = {}) {
  if (!encounterId || !patientId) throw new Error('saveHandover requires encounterId and patientId');
  if (!shift) throw new Error('saveHandover requires shift (M|E|N)');
  const { medplum } = await getMedplum();
  const me = await selfRef(medplum);
  const dateStr = date || localDateStr(new Date());
  const div = wrapXhtml(html || '');

  return medplum.createResource({
    resourceType: 'Composition',
    status: 'final',
    type: { text: 'nursing-handover' },
    subject: { reference: `Patient/${patientId}` },
    encounter: { reference: `Encounter/${encounterId}` },
    date: new Date().toISOString(),
    author: [me],
    title: `Shift handover — ${dateStr} ${shift}`,
    extension: shiftExt(shift),
    section: [{ title: `Handover (${shift})`, text: { status: 'additional', div } }],
  });
}

/**
 * saveRoundNote({ encounterId, patientId, html, aiOrigin? }) → Composition
 *
 * The doctor's bedside-round progress note (Wave 4 — the doctor-file wave). It is
 * the exact `saveHandover` idiom with a doctor-round type: a `Composition`
 * type:{text:'doctor-round'}, status:'final', author = the logged-in doctor's own
 * Practitioner (own-token attribution, contract §2), ONE section carrying the
 * confirmed HTML, and a dated title. When `aiOrigin` is passed (the note came from
 * a confirmed AI/voice round proposal) the AI-origin extension rides along so the
 * AI's real-world accuracy stays measurable — the doctor is still the author of
 * record (same idiom as every other ipd-live write). Fail loud.
 */
export async function saveRoundNote({ encounterId, patientId, html, aiOrigin } = {}) {
  if (!encounterId || !patientId) throw new Error('saveRoundNote requires encounterId and patientId');
  const body = String(html || '').trim();
  if (!body) throw new Error('The round note is empty');
  const { medplum } = await getMedplum();
  const me = await selfRef(medplum);
  const now = new Date();
  const dateStr = localDateStr(now);
  const timeStr = now.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
  const div = wrapXhtml(body);
  const ext = aiOriginExt(aiOrigin);

  return medplum.createResource({
    resourceType: 'Composition',
    status: 'final',
    type: { text: 'doctor-round' },
    subject: { reference: `Patient/${patientId}` },
    encounter: { reference: `Encounter/${encounterId}` },
    date: now.toISOString(),
    author: [me],
    title: `Doctor round — ${dateStr} ${timeStr}`,
    ...(ext.length ? { extension: ext } : {}),
    section: [{ title: 'Doctor round', text: { status: 'additional', div } }],
  });
}

// ════════════════════════════════════════════════════════════════════════════
// helpers
// ════════════════════════════════════════════════════════════════════════════

// ── order / due-item projections ────────────────────────────────────────────
// A flat MedicationRequest view the cardex + worklist render (contract §1/§3):
// { id, name, doseText, route, freq, prn, status, timeOfDay }. `resource` keeps
// the raw MedicationRequest for anything that needs the full dosageInstruction.
// Wave-2 ADDS (contract §7b, all nullable): authoredOn (order start), stoppedAt
// (the stop instant — extension first, meta.lastUpdated as fallback for orders
// stopped before the extension existed), priorPrescriptionId (replacement chain).
function projectOrder(o) {
  const di = (o.dosageInstruction && o.dosageInstruction[0]) || {};
  const freq = (di.timing && di.timing.code && di.timing.code.text) || null;
  const repeatTimes = (di.timing && di.timing.repeat && di.timing.repeat.timeOfDay) || [];
  const dar = (di.doseAndRate && di.doseAndRate[0]) || {};
  const doseText = dar.doseText
    || (dar.doseQuantity ? [dar.doseQuantity.value, dar.doseQuantity.unit].filter((x) => x != null).join(' ') : null)
    || null;
  const stoppedExt = (o.extension || []).find((e) => e && e.url === STOPPED_AT_EXT);
  return {
    id: o.id,
    name: (o.medicationCodeableConcept && o.medicationCodeableConcept.text) || null,
    doseText,
    route: (di.route && di.route.text) || null,
    freq,
    prn: di.asNeededBoolean === true || freq === 'PRN',
    status: o.status || 'active',
    // Normalise HH:mm:ss (FHIR time) back to HH:mm for the UI/due-engine.
    timeOfDay: (Array.isArray(repeatTimes) ? repeatTimes : []).map((t) => String(t).slice(0, 5)),
    authoredOn: o.authoredOn || null,
    stoppedAt: (stoppedExt && stoppedExt.valueDateTime)
      || (o.status === 'stopped' ? (o.meta && o.meta.lastUpdated) || null : null),
    priorPrescriptionId: refId(o.priorPrescription),
    // statusReason is a single CodeableConcept on MedicationRequest (R4 0..1);
    // tolerate a legacy array shape from older writes.
    stopReason: (Array.isArray(o.statusReason)
      ? (o.statusReason[0] && o.statusReason[0].text)
      : (o.statusReason && o.statusReason.text)) || null,
    resource: o,
  };
}
// AllergyIntolerance[] → [text]. Reads .code.text (OPD shape) or the first coding
// display. Empty/untexted allergies are dropped.
function allergyTextsOf(allergies) {
  return (allergies || [])
    .map((a) => (typeof a === 'string' ? a : (a && a.code && (a.code.text || (a.code.coding && a.code.coding[0] && a.code.coding[0].display)))))
    .filter(Boolean);
}
// ServiceRequest.occurrenceTiming.repeat.period(Unit) → hours (mirrors the pure
// engine's periodHoursOf; kept here so ipd-live doesn't reach into ipd-due internals).
function periodHoursOfSchedule(sr) {
  const rep = sr && sr.occurrenceTiming && sr.occurrenceTiming.repeat;
  if (!rep) return null;
  const period = Number(rep.period);
  if (!Number.isFinite(period) || period <= 0) return null;
  const unit = rep.periodUnit || 'h';
  if (unit === 'd') return period * 24;
  if (unit === 'min') return period / 60;
  return period;
}
// A MedicationAdministration with flat fields ADDED (the raw FHIR resource is
// preserved — the cardex reads .slotKey/.status/.effectiveDateTime/.request/
// .statusReason/.performer AND the digest reads the raw shape). Contract §3.
function projectAdministration(a) {
  const performerName = (a.performer && a.performer[0] && a.performer[0].actor && a.performer[0].actor.display) || null;
  const adjustExt = (a.extension || []).find((e) => e && e.url === DOSE_ADJUST_EXT);
  return {
    ...a,
    slotKey: slotKeyOf(a),
    // .status/.effectiveDateTime/.statusReason/.performer/.request already present on the raw resource.
    reason: (a.statusReason && a.statusReason[0] && a.statusReason[0].text) || null,
    performerName,
    // Wave-2: the this-dose-only adjustment (contract §7b) — the ACTUAL dose text
    // when it differed from the order, plus the nurse's note (both nullable).
    adjustedDose: (adjustExt && adjustExt.valueString) || null,
    noteText: (a.note && a.note[0] && a.note[0].text) || null,
  };
}
// Group vital Observations into newest-first rows keyed by slotKey (a scheduled
// round) or effective time (ad-hoc), each { effectiveDateTime, values:{sbp,dbp,
// hr,rr,temp,spo2} } — contract §3 (Agent D's recent-vitals table).
function vitalsRowsOf(observations) {
  const rows = new Map();
  const keyOf = (o) => slotKeyOf(o) || (o.effectiveDateTime || '');
  const codeIs = (o, code) => (o.code && o.code.coding || []).some((c) => c.code === code);
  const comp = (o, code) => (o.component || []).find((c) => (c.code && c.code.coding || []).some((x) => x.code === code));
  for (const o of observations || []) {
    const k = keyOf(o);
    if (!k) continue;
    if (!rows.has(k)) rows.set(k, { effectiveDateTime: o.effectiveDateTime || null, values: {} });
    const row = rows.get(k);
    if (!row.effectiveDateTime && o.effectiveDateTime) row.effectiveDateTime = o.effectiveDateTime;
    if (codeIs(o, '85354-9')) {
      const s = comp(o, '8480-6'); const d = comp(o, '8462-4');
      if (s && s.valueQuantity) row.values.sbp = s.valueQuantity.value;
      if (d && d.valueQuantity) row.values.dbp = d.valueQuantity.value;
    }
    const single = (code, key) => { if (codeIs(o, code) && o.valueQuantity) row.values[key] = o.valueQuantity.value; };
    single('8867-4', 'hr'); single('9279-1', 'rr'); single('59408-5', 'spo2'); single('8310-5', 'temp');
  }
  return [...rows.values()].sort((a, b) =>
    String(b.effectiveDateTime || '').localeCompare(String(a.effectiveDateTime || '')));
}

// The MedicationRequest id a dose due-item points at. A scheduled slot's slotKey
// is `dose:{orderId}:{date}T{hm}`; a PRN action item's slotKey is `prn:{orderId}`.
function orderIdOfDueItem(item) {
  const ref = item.ref && item.ref.reference;
  if (ref && ref.startsWith('MedicationRequest/')) return ref.split('/')[1];
  const sk = item.slotKey || '';
  if (sk.startsWith('dose:')) return sk.split(':')[1];
  if (sk.startsWith('prn:')) return sk.slice('prn:'.length);
  return null;
}

function hasPhysType(loc, code) {
  return ((loc.physicalType && loc.physicalType.coding) || []).some((c) => c.system === WARD_PHYS && c.code === code);
}
function bedIdentifierOf(loc) {
  const hit = (loc.identifier || []).find((i) => i.system === BED_ID_SYSTEM);
  return (hit && hit.value) || null;
}
function refId(ref) {
  const r = (ref && ref.reference) || (typeof ref === 'string' ? ref : '');
  return r ? r.split('/')[1] : null;
}
function normRef(ref) {
  if (typeof ref === 'string') return { reference: ref };
  if (ref && ref.reference) return ref;
  return ref;
}
// The active bed location entries of an Encounter (status 'active'), each a ref.
function activeLocationRefs(enc) {
  return ((enc && enc.location) || []).filter((l) => l.status === 'active' && l.location).map((l) => l.location);
}

// Resolve a bed's department org (OPD stamps Encounter.serviceProvider = the dept
// org). A bed's partOf points at its ward Location; the ward's ... we walk up to a
// managingOrganization if present, else the ward's own dept via its name/identifier
// is not reliable, so we return null and let the admission proceed unstamped.
async function resolveBedDepartment(medplum, bed) {
  // Prefer an explicit managingOrganization on the bed or its ward.
  if (bed.managingOrganization) return bed.managingOrganization;
  const wardRef = bed.partOf;
  if (!wardRef) return null;
  const ward = await medplum.readReference(wardRef).catch(() => null);
  if (ward && ward.managingOrganization) return ward.managingOrganization;
  return null;
}

function buildNewPatient({ name, sex, birthDate, ageYears, mrn }) {
  const p = { resourceType: 'Patient', name: humanName(name) };
  if (sex) p.gender = String(sex).toLowerCase();
  if (birthDate) p.birthDate = birthDate;
  else if (num(ageYears) != null) {
    const y = new Date().getFullYear() - num(ageYears);
    p.birthDate = `${y}-01-01`;
  }
  if (mrn) p.identifier = [{ system: MRN_SYSTEM, value: String(mrn) }];
  return p;
}
function humanName(display) {
  const parts = String(display || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return [{ text: String(display || '') }];
  const family = parts.length > 1 ? parts[parts.length - 1] : undefined;
  const given = parts.length > 1 ? parts.slice(0, -1) : parts;
  return [{ text: display, given, ...(family ? { family } : {}) }];
}
function buildAllergy(patientId, text) {
  return {
    resourceType: 'AllergyIntolerance',
    clinicalStatus: { coding: [{ system: 'http://terminology.hl7.org/CodeSystem/allergyintolerance-clinical', code: 'active' }] },
    code: { text },
    patient: { reference: `Patient/${patientId}` },
  };
}
// A dose string like '500 mg' → a doseQuantity; unparseable → null (caller keeps
// the raw doseText). Deliberately conservative — only split a leading number+unit.
function parseDose(text) {
  const m = String(text || '').trim().match(/^([\d.]+)\s*([a-zA-Zµμ/%]+)?/);
  if (!m) return null;
  const value = Number(m[1]);
  if (!Number.isFinite(value)) return null;
  return { value, ...(m[2] ? { unit: m[2] } : {}) };
}
function bumpVersion(v) {
  const n = Number(v);
  if (Number.isFinite(n)) return String(n + 1);
  const m = String(v || '').match(/(\d+)\s*$/);
  return m ? String(v).replace(/(\d+)\s*$/, String(Number(m[1]) + 1)) : '2';
}
function questionnaireCanonical(questionnaire) {
  if (!questionnaire) return null;
  if (typeof questionnaire === 'string') return questionnaire;
  const url = questionnaire.url || NURSING_ASSESSMENT_URL;
  return questionnaire.version ? `${url}|${questionnaire.version}` : url;
}
// answers: { linkId → value | value[] } → QuestionnaireResponse.item[]. Value type
// is inferred: boolean → valueBoolean, number → valueInteger, string → valueString,
// array → one answer entry per element (multi-select choice).
function answersToItems(answers) {
  const items = [];
  for (const [linkId, raw] of Object.entries(answers || {})) {
    const values = Array.isArray(raw) ? raw : [raw];
    const answer = values.filter((v) => v !== undefined && v !== null && v !== '').map(toAnswer);
    if (answer.length) items.push({ linkId, answer });
  }
  return items;
}
function toAnswer(v) {
  if (typeof v === 'boolean') return { valueBoolean: v };
  if (typeof v === 'number') return Number.isInteger(v) ? { valueInteger: v } : { valueDecimal: v };
  if (v && typeof v === 'object' && (v.code || v.display)) return { valueCoding: v };
  return { valueString: String(v) };
}
function wrapXhtml(html) {
  const body = String(html || '').trim() || '<p>No handover recorded.</p>';
  // If the caller already gave a full XHTML div, keep it; else wrap.
  if (/^<div[\s>]/i.test(body)) return body;
  return `<div xmlns="http://www.w3.org/1999/xhtml">${body}</div>`;
}

// ── Wave-2: the whole-stay timeline (contract §7b) ──────────────────────────
// Fold everything getBedContext already fetched into ONE chronological stream,
// newest first. Pure assembly — no extra reads. Event shape documented on
// getBedContext. Inputs are the PROJECTIONS (orders/administrations) + raw
// assessments/handovers + the grouped vitalsRows.
export function buildTimeline({ encounter, orders = [], administrations = [], vitalsRows = [], assessments = [], handovers = [], roundNotes = [], notes = [] }) {
  const ev = [];

  // Admission / discharge anchors.
  const ps = encounter && encounter.period && encounter.period.start;
  if (ps) {
    ev.push({
      id: `admit:${encounter.id}`, kind: 'admission', at: ps,
      title: 'Admitted to the ward',
      detail: reasonTextOf(encounter),
      by: attendingNameOf(encounter),
    });
  }
  const pe = encounter && encounter.period && encounter.period.end;
  if (pe && encounter.status === 'finished') {
    ev.push({ id: `discharge:${encounter.id}`, kind: 'discharge', at: pe, title: 'Discharged', detail: null, by: null });
  }

  // Order lifecycle: start (authoredOn — replacements say what they replace) and
  // stop (the stopped-at instant + reason). Orders without authoredOn (pre-wave-2
  // seeds) simply contribute no start event — the admission anchors them.
  const orderById = new Map(orders.map((o) => [o.id, o]));
  for (const o of orders) {
    const line = [o.name, o.doseText, o.route, o.freq].filter(Boolean).join(' ');
    if (o.authoredOn) {
      const prior = o.priorPrescriptionId ? orderById.get(o.priorPrescriptionId) : null;
      ev.push({
        id: `order-start:${o.id}`, kind: 'order', at: o.authoredOn, status: 'started',
        title: `Order started — ${line}`,
        detail: o.priorPrescriptionId
          ? `Replaces ${prior ? [prior.name, prior.doseText, prior.route, prior.freq].filter(Boolean).join(' ') : 'an earlier order'}`
          : null,
        by: null, ref: { reference: `MedicationRequest/${o.id}` },
      });
    }
    if (o.stoppedAt) {
      ev.push({
        id: `order-stop:${o.id}`, kind: 'order', at: o.stoppedAt, status: 'stopped',
        title: `Order stopped — ${line}`,
        detail: o.stopReason,
        by: null, ref: { reference: `MedicationRequest/${o.id}` },
      });
    }
  }

  // Dose ticks — given ✓ (with any this-dose adjustment) / held + reason.
  for (const a of administrations) {
    if (!a.effectiveDateTime) continue;
    const given = a.status === 'completed';
    const drug = (a.medicationCodeableConcept && a.medicationCodeableConcept.text) || null;
    const doseLine = (a.dosage && a.dosage.text) || drug || 'Medication';
    ev.push({
      id: `dose:${a.id}`, kind: 'dose', at: a.effectiveDateTime,
      status: given ? 'given' : 'held',
      title: doseLine,
      detail: given
        ? (a.adjustedDose ? `Adjusted dose — ${a.adjustedDose} given${a.noteText ? ` · ${a.noteText}` : ''}` : null)
        : (a.reason || 'not given'),
      by: a.performerName, ref: { reference: `MedicationAdministration/${a.id}` },
    });
  }

  // Vitals rounds — one event per grouped row, with the compact values.
  for (const row of vitalsRows) {
    if (!row.effectiveDateTime) continue;
    ev.push({
      id: `vitals:${row.effectiveDateTime}`, kind: 'vitals', at: row.effectiveDateTime,
      title: 'Vitals recorded', detail: null, by: null, values: row.values,
    });
  }

  // Shift assessments — with shift + Braden/Morse totals off the QR extensions.
  for (const qr of assessments) {
    if (!qr.authored) continue;
    const exts = qr.extension || [];
    const intOf = (url) => { const h = exts.find((e) => e.url === url && e.valueInteger != null); return h ? h.valueInteger : null; };
    const shiftHit = exts.find((e) => e.url === SHIFT_EXT);
    const braden = intOf(BRADEN_SCORE_EXT);
    const morse = intOf(MORSE_SCORE_EXT);
    ev.push({
      id: `assess:${qr.id}`, kind: 'assessment', at: qr.authored,
      shift: (shiftHit && shiftHit.valueCode) || null,
      title: 'Shift assessment',
      detail: [braden != null ? `Braden ${braden}` : null, morse != null ? `Morse ${morse}` : null].filter(Boolean).join(' · ') || null,
      by: (qr.author && qr.author.display) || null,
      scores: (braden != null || morse != null) ? { ...(braden != null ? { braden } : {}), ...(morse != null ? { morse } : {}) } : null,
      ref: { reference: `QuestionnaireResponse/${qr.id}` },
    });
  }

  // Handovers — the confirmed shift note.
  for (const c of handovers) {
    if (!c.date) continue;
    const shiftHit = (c.extension || []).find((e) => e.url === SHIFT_EXT);
    ev.push({
      id: `handover:${c.id}`, kind: 'handover', at: c.date,
      shift: (shiftHit && shiftHit.valueCode) || null,
      title: c.title || 'Shift handover',
      detail: null,
      by: (c.author && c.author[0] && c.author[0].display) || null,
      ref: { reference: `Composition/${c.id}` },
    });
  }

  // Doctor round notes (Wave-4). Projected as a first-class kind:'round' — the
  // timeline lane renders it as a full-width span with the doctor/prescription
  // indigo accent, distinct from the neutral handover span (contract §7).
  for (const c of roundNotes) {
    if (!c.date) continue;
    ev.push({
      id: `round:${c.id}`, kind: 'round', at: c.date,
      title: c.title || 'Doctor round',
      detail: null,
      by: (c.author && c.author[0] && c.author[0].display) || null,
      ref: { reference: `Composition/${c.id}` },
    });
  }

  // Free-text nurse notes (recordNursingNote) — a plain Observation carrying the
  // spoken/typed clinical statement in valueString. Rendered as kind:'note' (a
  // full-width span, contract §9b). The FULL text rides in `detail`: the timeline
  // card auto-truncates the collapsed line (CSS ellipsis) and the expandable strip
  // shows it all (buildStrip reads e.detail) — no manual truncation needed.
  for (const o of notes) {
    if (!o.effectiveDateTime) continue;
    const body = String(o.valueString || '').trim();
    if (!body) continue;
    ev.push({
      id: `note:${o.id}`, kind: 'note', at: o.effectiveDateTime,
      title: 'Nurse note',
      detail: body,
      by: (o.performer && o.performer[0] && o.performer[0].display) || null,
      ref: { reference: `Observation/${o.id}` },
    });
  }

  // Newest first (the whole-stay stream). Stable tie-break on id.
  return ev.sort((a, b) => String(b.at).localeCompare(String(a.at)) || String(a.id).localeCompare(String(b.id)));
}

// Wave-2: the newest filed Braden/Morse totals (glance-header risk chips) —
// read off the QuestionnaireResponse score extensions, newest authored first.
function latestScoresOf(assessments) {
  const sorted = [...(assessments || [])].sort((a, b) =>
    String(b.authored || '').localeCompare(String(a.authored || '')));
  const pick = (url) => {
    for (const r of sorted) {
      const hit = ((r && r.extension) || []).find((e) => e.url === url && e.valueInteger != null);
      if (hit) return { value: hit.valueInteger, at: r.authored || null };
    }
    return null;
  };
  return { braden: pick(BRADEN_SCORE_EXT), morse: pick(MORSE_SCORE_EXT) };
}

// Latest vitals digest across a set of Observations (same read logic as OPD's
// getLatestVitals — BP panel components + simple quantities), for the glance
// header. Returns null when there is nothing.
function latestVitalsOf(observations) {
  const sorted = [...(observations || [])].sort((a, b) =>
    String(b.effectiveDateTime || '').localeCompare(String(a.effectiveDateTime || '')));
  const firstWith = (pred) => sorted.find(pred);
  const codeIs = (o, code) => (o.code && o.code.coding || []).some((c) => c.code === code);
  const bp = firstWith((o) => codeIs(o, '85354-9'));
  const comp = (code) => bp && (bp.component || []).find((c) => (c.code && c.code.coding || []).some((x) => x.code === code));
  const compVal = (code) => { const c = comp(code); return c && c.valueQuantity ? c.valueQuantity.value : null; };
  const qv = (code) => { const o = firstWith((x) => codeIs(x, code)); return o && o.valueQuantity ? o.valueQuantity.value : null; };
  const sbp = compVal('8480-6');
  const dbp = compVal('8462-4');
  const hr = qv('8867-4');
  const rr = qv('9279-1');
  const spo2 = qv('59408-5');
  const temp = qv('8310-5');
  const capturedAt = (sorted[0] && sorted[0].effectiveDateTime) || null;
  const any = sbp != null || dbp != null || hr != null || rr != null || spo2 != null || temp != null;
  if (!any) return null;
  // Both a flat `bp` string (digest/back-compat) AND a `.values` object + a
  // `.effectiveDateTime` alias — the glance-header VitalsStrip reads .values.* .
  return {
    bp: `${sbp ?? '—'}/${dbp ?? '—'}`,
    hr, rr, spo2, temp,
    values: { sbp, dbp, hr, rr, temp, spo2 },
    capturedAt,
    effectiveDateTime: capturedAt,
  };
}

// ════════════════════════════════════════════════════════════════════════════
// W4 — IPD patients registry helpers (additive; contract §7d /ward/patients).
// Kept at the file BOTTOM, purely additive, so the W2/W4 same-wave merge stays
// trivial. Read-only, run-as-the-user (RLS the only lock) like every read above.
// ════════════════════════════════════════════════════════════════════════════

/**
 * listAdmissions({ status='finished', cursor, count=20 }) → { rows, nextCursor }
 * The IPD patient registry's "Past admissions" feed (contract §7d): finished IMP
 * Encounters, newest first (_sort=-date), _count-bounded with a load-more cursor.
 * Each row is a flat projection { encounterId, patient:{id,displayName,age,sex},
 * bedLabel, periodStart, periodEnd, lengthOfStayDays, condition, attendingName }
 * so the screen renders without a second read. `status:'in-progress'` returns the
 * live admissions with the SAME shape (a convenience; "In ward now" uses
 * listWardBeds, but this keeps one projection for both). `cursor` is the Medplum
 * bundle `next` link URL returned as nextCursor — pass it back to page forward.
 */
export async function listAdmissions({ status = 'finished', cursor, count = 20 } = {}) {
  const { medplum } = await getMedplum();
  // A cursor is a full Medplum `next` URL — follow it verbatim so paging is exact
  // (offset + sort preserved). Otherwise start a fresh, subject-included search.
  const bundle = cursor
    ? await medplum.get(cursor)
    : await medplum.search('Encounter', {
      status, class: 'IMP', _include: 'Encounter:subject', _sort: '-date', _count: count,
    });
  const entries = (bundle && bundle.entry) || [];
  const encounters = entries
    .filter((e) => e.resource && e.resource.resourceType === 'Encounter' && (!e.search || e.search.mode !== 'include'))
    .map((e) => e.resource);
  const patientById = new Map(entries
    .filter((e) => e.resource && e.resource.resourceType === 'Patient')
    .map((e) => [e.resource.id, e.resource]));

  const rows = encounters.map((enc) => {
    const pid = refId(enc.subject);
    const p = pid ? patientById.get(pid) : null;
    const start = (enc.period && enc.period.start) || null;
    const end = (enc.period && enc.period.end) || null;
    return {
      encounterId: enc.id,
      status: enc.status,
      patient: p
        ? { id: p.id, displayName: displayNameOf(p) || 'Patient', age: ageFromBirthDate(p.birthDate), sex: sexOf(p) }
        : { id: pid, displayName: (enc.subject && enc.subject.display) || 'Patient', age: null, sex: null },
      bedLabel: bedLabelOfEncounter(enc),
      periodStart: start,
      periodEnd: end,
      lengthOfStayDays: lengthOfStay(start, end),
      condition: reasonTextOf(enc),
      attendingName: attendingNameOf(enc),
      resource: enc,
    };
  });

  const nextLink = ((bundle && bundle.link) || []).find((l) => l.relation === 'next');
  return { rows, nextCursor: (nextLink && nextLink.url) || null };
}

/**
 * latestScoresForPatients(patientIds) → Map(patientId → { braden?, morse? })
 * The most-recent Braden/Morse total per patient in ONE bounded search (the
 * scores are written by saveShiftAssessment as plain Observations with code.text
 * braden-score / morse-score, valueInteger — contract §2). Cheap: a single
 * `code:text` search across the admitted set, newest-first, first value per
 * patient/code wins. Empty ids → empty map. Best-effort — a score miss never
 * blocks the registry (the chip simply doesn't render).
 */
export async function latestScoresForPatients(patientIds) {
  const map = new Map();
  const ids = [...new Set((patientIds || []).filter(Boolean))];
  if (!ids.length) return map;
  const { medplum } = await getMedplum();
  const rows = await medplum.searchResources('Observation', {
    subject: ids.map((id) => `Patient/${id}`).join(','),
    'code:text': 'braden-score,morse-score',
    _sort: '-date', _count: 200,
  }).catch(() => []);
  for (const o of rows) {
    const pid = refId(o.subject);
    if (!pid || o.valueInteger == null) continue;
    const text = (o.code && o.code.text) || '';
    const key = text === 'braden-score' ? 'braden' : text === 'morse-score' ? 'morse' : null;
    if (!key) continue;
    if (!map.has(pid)) map.set(pid, {});
    const rec = map.get(pid);
    if (rec[key] == null) rec[key] = o.valueInteger; // newest-first → first value wins
  }
  return map;
}

// The active (or first) bed label an Encounter occupies, from its location[] entry
// display (prefer the active entry; a finished encounter's entry is 'completed').
function bedLabelOfEncounter(enc) {
  const locs = (enc && enc.location) || [];
  const active = locs.find((l) => l.status === 'active') || locs[0];
  return (active && active.location && active.location.display) || null;
}
// Length of stay in whole days (admit day = day 1). Open stay (no end) → to now.
function lengthOfStay(startISO, endISO) {
  if (!startISO) return null;
  const s = new Date(startISO);
  if (Number.isNaN(s.getTime())) return null;
  const e = endISO ? new Date(endISO) : new Date();
  if (Number.isNaN(e.getTime())) return null;
  const days = Math.floor((new Date(e).setHours(0, 0, 0, 0) - new Date(s).setHours(0, 0, 0, 0)) / 86400000) + 1;
  return days >= 1 ? days : 1;
}

// ════════════════════════════════════════════════════════════════════════════
// V1 — WAVE 3 §8a fold-ins (Command dock). Both additive, backward-compatible.
// ════════════════════════════════════════════════════════════════════════════

// The IPD Questionnaire url prefix + the template-kind extension (contract §7d).
// Kept here (mirrors TemplateEditor's local constants) so listIpdTemplates is the
// ONE canonical broadened template loader — TemplateEditor switches to it.
const IPD_Q_URL_PREFIX = 'https://healther.io/ipd/questionnaire/';
const TEMPLATE_KIND_EXT = 'https://healther.io/fhir/ipd/template-kind';

/**
 * recordNursingNote({ encounterId, patientId, text }) → Observation (V1, §8a).
 *
 * The free-text nurse note voice-capture used to shape inline (its local
 * writeNursingNote). Moved here so voice-capture AND the command dock write it
 * through the SAME ipd-live door as every other clinical write — attributed to
 * the nurse's own Practitioner via their own token (contract §2), fail loud.
 * A plain `Observation {code:{text:'nursing-note'}, valueString}` on the
 * encounter is the simplest FHIR-correct target for a spoken clinical statement.
 */
export async function recordNursingNote({ encounterId, patientId, text, aiOrigin } = {}) {
  const body = String(text || '').trim();
  if (!body) throw new Error('The note is empty');
  if (!encounterId || !patientId) throw new Error('recordNursingNote requires encounterId and patientId');
  const { medplum } = await getMedplum();
  const me = await selfRef(medplum);
  const ext = aiOriginExt(aiOrigin);
  return medplum.createResource({
    resourceType: 'Observation',
    status: 'final',
    code: { text: 'nursing-note' },
    subject: { reference: `Patient/${patientId}` },
    encounter: { reference: `Encounter/${encounterId}` },
    effectiveDateTime: new Date().toISOString(),
    performer: [me],
    valueString: body,
    ...(ext.length ? { extension: ext } : {}),
  });
}

/**
 * listIpdTemplates() → Questionnaire[] each carrying a flat `kind` (V1, §8a/§7d).
 *
 * ALL active Questionnaires whose canonical url sits under the IPD prefix — the
 * broadened list the TemplateEditor renders (nursing-assessment form + the W4
 * starter pack: symptom sets, checklists). `kind` is the template-kind extension's
 * valueCode ('assessment'|'symptom'|'checklist'), defaulting to 'assessment' for
 * the pre-pack form that predates the extension. The raw Questionnaire is returned
 * intact with `.kind` ADDED, so existing consumers keep working.
 * (`listAssessmentTemplates` keeps its NARROW meaning — the nursing-assessment url
 *  only — for ShiftAssessment.)
 */
export async function listIpdTemplates() {
  const { medplum } = await getMedplum();
  const all = await medplum.searchResources('Questionnaire',
    { status: 'active', _sort: '-_lastUpdated', _count: 100 });
  return all
    .filter((q) => String(q.url || '').startsWith(IPD_Q_URL_PREFIX))
    .map((q) => ({ ...q, kind: templateKindOf(q) }));
}
// The kind extension's valueCode; templates without it are 'assessment'.
function templateKindOf(q) {
  const e = ((q && q.extension) || []).find((x) => x && x.url === TEMPLATE_KIND_EXT);
  const v = e && e.valueCode;
  return v === 'symptom' || v === 'checklist' || v === 'assessment' ? v : 'assessment';
}

// ── Y2 (contract §10b.2) — read the hospital agent lexicon ────────────────────
// The agent-lexicon Bot (bots/agent-lexicon.cjs) recompiles ONE Basic on every
// IPD-template change: template titles/kinds, item labels + option codes/displays,
// and the drug names on active MedicationRequests (the formulary in use). The
// command dock loads this to BOOST matching + med-name normalisation, so a
// template edit teaches the agent new vocabulary with no code change / redeploy.
//
// Shape (matches the Bot's payload):
//   { templates:[{ url, title, kind, items:[{ linkId, label, options:[{code,display}] }] }],
//     drugsInUse:[string], compiledAt:ISO } | null (no lexicon compiled yet).
//
// Additive + non-throwing on the "not present yet" case ONLY: if the Bot has never
// run (no Basic), we return null so a caller degrades gracefully to the pure
// matcher — this is NOT a speculative fallback masking a bug (AGENTS §1.3); a
// missing lexicon is an expected state before the first template edit, and the
// matcher already works template-driven without it. A genuine read ERROR (403 /
// network) still THROWS via getMedplum()/searchOne, per the fail-loud rule.
const AGENT_LEXICON_IDENTIFIER = 'https://healther.io/ipd/agent-lexicon';
export async function getAgentLexicon() {
  const { medplum } = await getMedplum();
  const basic = await medplum.searchOne('Basic',
    { identifier: `${AGENT_LEXICON_IDENTIFIER}|agent-lexicon` });
  if (!basic) return null;
  const ext = ((basic.extension) || []).find((x) => x && x.url === AGENT_LEXICON_IDENTIFIER);
  const raw = ext && ext.valueString;
  if (!raw) return null;
  // A malformed payload is a real bug (the Bot wrote garbage) → fail loud.
  const parsed = JSON.parse(raw);
  return {
    templates: Array.isArray(parsed.templates) ? parsed.templates : [],
    drugsInUse: Array.isArray(parsed.drugsInUse) ? parsed.drugsInUse : [],
    compiledAt: parsed.compiledAt || null,
  };
}
